// Non-secret client-side identity hint, sibling to `sessionPresence`. The
// HttpOnly cookie remains the credential; this blob is decoration: it drives
// the "Welcome back, {name}" account-chooser screen so a returning user
// sees their own avatar/name/email instead of a generic signin form.
//
// Industry pattern: Google's account chooser, LinkedIn's "is this you?" —
// recognition + UX warmth, NOT a security shortcut. The chooser screen still
// requires password (and 2FA if enabled). This module owns the hint storage
// only; the chooser screen + password flow live in web-app.
//
// `lastSignedInAt` is whatever the server last reported as the active
// session's hydration time (usually now() when /me resolves). It's only used
// for the muted "Last signed in N minutes ago" footer; not security-relevant.

const STORAGE_KEY = 'mightybot:lastIdentity';

export interface IdentityHint {
	name: string | null;
	email: string;
	avatarUrl: string | null;
	orgName: string | null;
	provider: string | null;
	lastSignedInAt: string;
}

const readStorage = (): Storage | null => {
	try {
		return typeof window !== 'undefined' ? window.localStorage : null;
	} catch {
		return null;
	}
};

const isHintShape = (value: unknown): value is IdentityHint => {
	if (typeof value !== 'object' || value === null) return false;
	const v = value as Record<string, unknown>;
	// Required: email (the stable identifier) and lastSignedInAt (the only
	// non-nullable derived field). Optional name/avatar/org can be null.
	return (
		typeof v.email === 'string' &&
		v.email.length > 0 &&
		typeof v.lastSignedInAt === 'string'
	);
};

export const writeIdentityHint = (hint: IdentityHint): void => {
	readStorage()?.setItem(STORAGE_KEY, JSON.stringify(hint));
};

export const clearIdentityHint = (): void => {
	readStorage()?.removeItem(STORAGE_KEY);
};

export const readIdentityHint = (): IdentityHint | null => {
	const storage = readStorage();
	if (!storage) return null;
	const raw = storage.getItem(STORAGE_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!isHintShape(parsed)) {
			// Malformed / partial / future-schema. Self-heal so we don't keep
			// re-reading garbage on every mount.
			storage.removeItem(STORAGE_KEY);
			return null;
		}
		return {
			name: typeof parsed.name === 'string' ? parsed.name : null,
			email: parsed.email,
			avatarUrl: typeof parsed.avatarUrl === 'string' ? parsed.avatarUrl : null,
			orgName: typeof parsed.orgName === 'string' ? parsed.orgName : null,
			provider: typeof parsed.provider === 'string' ? parsed.provider : null,
			lastSignedInAt: parsed.lastSignedInAt,
		};
	} catch {
		storage.removeItem(STORAGE_KEY);
		return null;
	}
};
